/**
 * @file normalizeVolInteract.js
 * @description 卷互动区数据归一化（评论 id/messageId、isLike → isMyLike）
 * @module features/comic/utils/normalizeVolInteract
 */

import { ensureCommentMessageId } from '@/features/message/utils/normalizeMessageFields'

/** 卷互动区评论「查看更多」每次请求的页容量 */
export const VOL_INTERACT_LOAD_MORE_PAGE_SIZE = 5

/** 收起评论后展示的条数 */
export const VOL_INTERACT_COLLAPSED_COMMENT_LIMIT = 5

/**
 * 归一化 commentExtractVo 分页与评论列表字段
 * @param {object | null | undefined} extract
 * @returns {object | null | undefined}
 */
function normalizeCommentExtractVo(extract) {
  if (!extract) return extract
  const commentVos = (extract.commentVos || []).map((comment) =>
    ensureCommentMessageId(comment),
  )
  const totalCount = Number(extract.totalCount ?? extract.contentTotal ?? 0)
  return {
    ...extract,
    commentVos,
    contentTotal: Number(extract.contentTotal ?? totalCount ?? 0),
    totalCount,
    pageNum: Number(extract.pageNum ?? 1),
    pageSize: Number(extract.pageSize ?? commentVos.length),
    totalPages: Number(extract.totalPages ?? 1),
    hasNext: Boolean(extract.hasNext),
    hasPrev: Boolean(extract.hasPrev),
  }
}

/**
 * 归一化卷互动区接口 data（commentVos 内 isLike → isMyLike，id → messageId）
 * @param {object | null | undefined} data
 * @returns {object | null | undefined}
 */
export function normalizeVolInteractData(data) {
  if (!data?.commentExtractVo) return data
  return {
    ...data,
    commentExtractVo: normalizeCommentExtractVo(data.commentExtractVo),
  }
}

/**
 * 将分页加载的新评论合并进已有互动数据（按 messageId 去重）
 * @param {object | null | undefined} existingData
 * @param {object | null | undefined} newPageData
 * @returns {object | null | undefined}
 */
export function mergeVolInteractComments(existingData, newPageData) {
  if (!newPageData) return existingData
  const normalizedPage = normalizeVolInteractData(newPageData)
  if (!existingData?.commentExtractVo) return normalizedPage

  const existingVos = existingData.commentExtractVo.commentVos || []
  const newVos = normalizedPage.commentExtractVo?.commentVos || []
  const existingIds = new Set(
    existingVos.map((c) => String(c.messageId ?? c.id ?? '')),
  )
  const appended = newVos.filter((c) => {
    const id = String(c.messageId ?? c.id ?? '')
    return id && !existingIds.has(id)
  })

  return normalizeVolInteractData({
    ...existingData,
    ...normalizedPage,
    commentExtractVo: {
      ...existingData.commentExtractVo,
      ...normalizedPage.commentExtractVo,
      commentVos: [...existingVos, ...appended],
    },
  })
}

/**
 * 评论是否已被当前用户点赞（兼容 isMyLike / isLike）
 * @param {{ isMyLike?: boolean, isLike?: boolean } | null | undefined} comment
 * @returns {boolean}
 */
export function isVolCommentLiked(comment) {
  if (!comment) return false
  return Boolean(comment.isMyLike ?? comment.isLike)
}
